import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiConnectorGet, apiConnectorPost } from "../../services/apiconnector";
import { endpoint } from "../../services/urls";
import { enCryptData } from "../../shared/secret/index";
import toast from "react-hot-toast";

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  pageBg:      "#000000",
  cardBg:      "#0d1219",
  inputBg:     "#0a0f15",
  cyan:        "#00e5ff",
  cyanDim:     "rgba(0,229,255,0.35)",
  green:       "#21c15c",
  greenBtn:    "linear-gradient(135deg, #00b4be 0%, #21c15c 100%)",
  red:         "#ff5252",
  amber:       "#f59e0b",
  textPrimary: "#ffffff",
  textMuted:   "#9ca3af",
  textDim:     "#6b7280",
  border:      "1px solid rgba(0,229,255,0.2)",
  borderFocus: "1px solid rgba(0,229,255,0.55)",
};

function fmt(val) {
  const n = parseFloat(val || 0);
  return isNaN(n) ? "0.000" : n.toFixed(3);
}

function fmtDate(str) {
  if (!str) return "-";
  return new Date(str).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── History view ──────────────────────────────────────────────────────────────
function HistoryView() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["withdrawal_history", page],
    queryFn: () =>
      apiConnectorPost(endpoint.member_payout_report, {
        search:     "",
        created_at: "",
        updated_at: "",
        page,
        count:      "10",
      }),
    keepPreviousData: true,
  });

  const allData    = data?.data?.result || {};
  const rows       = allData?.data || [];
  const totalPages = Math.max(1, allData?.total_pages || 1);

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{
          width: "34px", height: "34px", borderRadius: "50%",
          border: `3px solid rgba(0,229,255,0.15)`, borderTopColor: C.cyan,
          animation: "spin 0.8s linear infinite", margin: "0 auto",
        }} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{ fontSize: "34px", marginBottom: "12px" }}>📭</div>
        <p style={{ color: C.textDim, fontSize: "14px" }}>No withdrawal history yet</p>
      </div>
    );
  }

  const statusColor = (s) => {
    if (s === "Success") return "#21c15c";
    if (s === "Failed")  return C.red;
    return C.amber;
  };

  return (
    <div>
      {rows.map((row, i) => (
        <div key={row?.tr11_transacton_id || i} style={{
          background: C.inputBg, borderRadius: "12px",
          border: "1px solid rgba(0,229,255,0.15)",
          padding: "14px", marginBottom: "10px",
        }}>
          {/* TX id + amount */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
            <span style={{ color: C.textDim, fontSize: "11px", fontFamily: "monospace" }}>
              #{row?.tr11_transacton_id || "-"}
            </span>
            <span style={{ color: C.cyan, fontWeight: "700", fontSize: "15px", fontFamily: "monospace" }}>
              ${fmt(row?.tr11_amont)} USDT
            </span>
          </div>

          {/* Date */}
          <p style={{ color: "rgba(160,220,235,0.75)", fontSize: "11px", marginBottom: "8px" }}>
            {fmtDate(row?.tr11_created_at)}
          </p>

          {/* Charges + Net + Status */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px", marginBottom: "8px" }}>
            <div style={s.infoChip}>
              <span style={s.chipLabel}>Charges</span>
              <span style={s.chipVal}>${fmt(row?.tr11_charges)}</span>
            </div>
            <div style={s.infoChip}>
              <span style={s.chipLabel}>Net</span>
              <span style={{ ...s.chipVal, color: C.green }}>${fmt(row?.tr11_net_amnt)}</span>
            </div>
            <div style={{
              ...s.infoChip,
              border: `1px solid ${statusColor(row?.tr11_status)}44`,
              background: `${statusColor(row?.tr11_status)}14`,
            }}>
              <span style={s.chipLabel}>Status</span>
              <span style={{ ...s.chipVal, color: statusColor(row?.tr11_status) }}>
                {row?.tr11_status || "Pending"}
              </span>
            </div>
          </div>

          {/* Wallet address */}
          {row?.tr11_payout_to && (
            <p style={{
              color: "rgba(190,235,245,0.75)", fontSize: "10px",
              background: "rgba(0,229,255,0.04)", borderRadius: "6px",
              padding: "5px 8px", border: "1px solid rgba(0,229,255,0.1)",
              marginBottom: "6px", wordBreak: "break-all",
            }}>
              {row.tr11_payout_to}
            </p>
          )}

          {/* Hash */}
          {row?.tr11_hash && (
            <button
              onClick={() => window.open(`https://bscscan.com/tx/${row.tr11_hash}`, "_blank")}
              style={{
                background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.2)",
                borderRadius: "6px", color: C.cyan,
                fontSize: "10px", padding: "4px 10px",
                cursor: "pointer", fontFamily: "monospace",
              }}
            >
              🔗 {row.tr11_hash.substring(0, 14)}…
            </button>
          )}
        </div>
      ))}

      {/* Pagination */}
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", marginTop: "16px" }}>
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          style={{
            background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.2)",
            borderRadius: "8px", color: page === 1 ? "#374151" : C.cyan,
            fontSize: "13px", padding: "7px 16px",
            cursor: page === 1 ? "not-allowed" : "pointer",
          }}
        >← Prev</button>
        <span style={{ color: C.textDim, fontSize: "12px" }}>{page} / {totalPages}</span>
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          style={{
            background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.2)",
            borderRadius: "8px", color: page === totalPages ? "#374151" : C.cyan,
            fontSize: "13px", padding: "7px 16px",
            cursor: page === totalPages ? "not-allowed" : "pointer",
          }}
        >Next →</button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Withdrawal() {
  const queryClient                 = useQueryClient();
  const [view, setView]             = useState("form");
  const [walletAddress, setWallet]  = useState("");
  const [amount, setAmount]         = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: profileData } = useQuery({
    queryKey: ["member_details_withdrawal"],
    queryFn:  () => apiConnectorGet(endpoint.member_details),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const profile     = profileData?.data?.result?.[0] || {};
  const fundBalance = parseFloat(profile?.tr03_fund_wallet || 0);
  const isOverBalance = amount !== "" && Number(amount) > fundBalance;

  useEffect(() => {
    if (profile?.lgn_wallet_add) setWallet(profile?.lgn_wallet_add);
  }, [profile?.lgn_wallet_add]);

  const validate = () => {
    if (!walletAddress.trim()) {
      toast.error("Please enter your BEP20 wallet address");
      return false;
    }
    if (!amount || Number(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return false;
    }
    if (Number(amount) < 1) {
      toast.error("Minimum withdrawal amount is $10");
      return false;
    }
    if (isOverBalance) {
      toast.error("Insufficient fund wallet balance");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setShowConfirm(false);
    setSubmitting(true);
    const reqBody = {
      wallet_address: walletAddress.trim(),
      user_amount:    Number(amount).toFixed(3),
      wallet_type:    "fund_wallet",
    };
    const res = await apiConnectorPost(endpoint.member_payout, {
      payload: enCryptData(reqBody),
    });
    setSubmitting(false);
    if (res?.data?.success) {
      toast.success(res.data.message || "Withdrawal request submitted!");
      setWallet("");
      setAmount("");
      queryClient.invalidateQueries({ queryKey: ["withdrawal_history"] });
      queryClient.invalidateQueries({ queryKey: ["member_details_withdrawal"] });
    } else {
      toast.error(res?.data?.message || "Withdrawal failed. Please try again.");
    }
  };

  const quickAmounts = [25, 50, 100];

  return (
    <div style={{
      minHeight: "100vh", background: C.pageBg,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "20px 16px 100px",
    }}>
      <div style={{
        width: "100%", maxWidth: "420px", background: C.cardBg,
        borderRadius: "20px", border: "1px solid rgba(0,229,255,0.2)",
        boxShadow: "0 8px 40px rgba(0,0,0,0.7), 0 0 60px rgba(0,180,190,0.05)",
        position: "relative", overflow: "hidden",
      }}>

        {/* Accent strip */}
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0, width: "3px",
          background: `linear-gradient(to bottom, #59e1e8, #00b4be, #21c15c)`,
        }} />

        {/* Corner accents */}
        <div style={{
          position: "absolute", top: 0, right: 0, width: "72px", height: "72px",
          borderTop: "2px solid rgba(0,229,255,0.2)",
          borderRight: "2px solid rgba(0,229,255,0.2)",
          borderRadius: "0 20px 0 0",
        }} />
        <div style={{
          position: "absolute", bottom: 0, left: 0, width: "56px", height: "56px",
          borderBottom: "2px solid rgba(33,193,92,0.15)",
          borderLeft: "2px solid rgba(33,193,92,0.15)",
          borderRadius: "0 0 0 20px",
        }} />

        {/* Glow blobs */}
        <div style={{
          position: "absolute", top: -40, right: -40, width: 200, height: 200,
          borderRadius: "50%", background: "rgba(0,180,190,0.07)",
          filter: "blur(40px)", pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: -40, left: -20, width: 160, height: 160,
          borderRadius: "50%", background: "rgba(33,193,92,0.04)",
          filter: "blur(30px)", pointerEvents: "none",
        }} />

        <div style={{ padding: "28px 24px 28px 28px", position: "relative", zIndex: 1 }}>

          {/* ── Header ── */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "20px" }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                <span style={{
                  width: "10px", height: "10px", borderRadius: "50%",
                  background: C.cyan, boxShadow: `0 0 8px ${C.cyan}`,
                  flexShrink: 0, animation: "pulse 2s infinite",
                }} />
                <span style={{ fontSize: "20px", fontWeight: "800", color: C.cyan, letterSpacing: "-0.3px" }}>
                  Withdrawal
                </span>
                <span style={{ fontSize: "11px", color: C.textDim, fontWeight: "500" }}>USDT·BEP20</span>
              </div>
              <p style={{ color: C.textDim, fontSize: "12px", marginLeft: "20px" }}>
                Request payout from your Fund Wallet
              </p>
            </div>
            <button
              onClick={() => setView(view === "form" ? "history" : "form")}
              style={{
                flexShrink: 0, marginLeft: "12px",
                background: view === "history" ? "rgba(0,229,255,0.14)" : "rgba(0,229,255,0.06)",
                border: "1px solid rgba(0,229,255,0.3)",
                borderRadius: "10px", color: C.cyan,
                fontSize: "12px", fontWeight: "600",
                padding: "7px 13px", cursor: "pointer",
                display: "flex", alignItems: "center", gap: "5px",
                whiteSpace: "nowrap",
              }}
            >
              {view === "form" ? <>📋 History</> : <>← Back</>}
            </button>
          </div>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "22px" }}>
            <div style={{ flex: 1, height: "1px", background: "rgba(0,229,255,0.15)" }} />
            <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: C.cyan, boxShadow: `0 0 5px ${C.cyan}` }} />
            <div style={{ flex: 1, height: "1px", background: "rgba(0,229,255,0.15)" }} />
          </div>

          {view === "history" ? <HistoryView /> : (
            <>
              {/* Fund wallet balance */}
              <div style={{
                background: "linear-gradient(135deg, rgba(33,193,92,0.08) 0%, rgba(0,180,190,0.06) 100%)",
                border: "1px solid rgba(33,193,92,0.25)",
                borderRadius: "14px", padding: "14px 16px", marginBottom: "18px",
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "16px" }}>🏦</span>
                    <span style={{ color: C.textMuted, fontSize: "12px", fontWeight: "600" }}>Fund Wallet</span>
                  </div>
                  <div>
                    <span style={{ color: C.green, fontWeight: "700", fontSize: "18px" }}>
                      ${fmt(profile?.tr03_fund_wallet)}
                    </span>
                    <span style={{ color: C.textDim, fontSize: "10px", marginLeft: "4px" }}>USDT</span>
                  </div>
                </div>
              </div>

              {/* Wallet address */}
              <label style={s.label}>Wallet Address (USDT·BEP20)</label>
              <div style={{ position: "relative", marginBottom: "14px" }}>
                <input
                  type="text"
                  value={walletAddress}
                  readOnly
                  style={{
                    ...s.input,
                    paddingRight: "44px",
                    cursor: "not-allowed",
                    opacity: 0.85,
                    border: "1px solid rgba(0,229,255,0.15)",
                  }}
                />
                <span style={{
                  position: "absolute", right: "14px", top: "50%",
                  transform: "translateY(-50%)", fontSize: "15px",
                  pointerEvents: "none",
                }}>🔒</span>
              </div>

              {/* Amount */}
              <label style={s.label}>
                Withdrawal Amount — Available: ${fmt(profile?.tr03_fund_wallet)}
              </label>
              <div style={{ position: "relative", marginBottom: isOverBalance ? "6px" : "0" }}>
                <input
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  min="0"
                  onChange={(e) => setAmount(e.target.value)}
                  style={{
                    ...s.input,
                    paddingRight: "70px",
                    border: isOverBalance ? "1px solid rgba(255,82,82,0.6)" : C.border,
                    marginBottom: 0,
                  }}
                  onFocus={(e) => { e.target.style.border = isOverBalance ? "1px solid rgba(255,82,82,0.8)" : C.borderFocus; }}
                  onBlur={(e)  => { e.target.style.border = isOverBalance ? "1px solid rgba(255,82,82,0.6)" : C.border; }}
                />
                <span style={{
                  position: "absolute", right: "14px", top: "50%",
                  transform: "translateY(-50%)",
                  color: C.textDim, fontSize: "12px", fontWeight: "600",
                  pointerEvents: "none",
                }}>USDT</span>
              </div>

              {isOverBalance && (
                <p style={{ color: C.red, fontSize: "11px", marginBottom: "4px", paddingLeft: "2px" }}>
                  ✕ Amount exceeds available balance
                </p>
              )}

              {/* Quick amount buttons */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "8px", marginTop: "10px", marginBottom: "18px" }}>
                {[...quickAmounts, "Max"].map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setAmount(amt === "Max" ? fmt(profile?.tr03_fund_wallet) : String(amt))}
                    style={{
                      padding: "8px 0",
                      background: "rgba(0,229,255,0.08)",
                      border: "1px solid rgba(0,229,255,0.25)",
                      borderRadius: "8px", color: C.cyan,
                      fontSize: "12px", fontWeight: "700",
                      cursor: "pointer", letterSpacing: "0.03em",
                    }}
                  >
                    {amt === "Max" ? "Max" : `$${amt}`}
                  </button>
                ))}
              </div>

              {/* Warning notice */}
              <div style={{
                display: "flex", alignItems: "flex-start", gap: "10px",
                background: "rgba(245,158,11,0.06)",
                border: "1px solid rgba(245,158,11,0.25)",
                borderRadius: "12px", padding: "12px 14px", marginBottom: "18px",
              }}>
                <span style={{ fontSize: "16px", flexShrink: 0 }}>⚠️</span>
                <div>
                  <p style={{ color: C.amber, fontSize: "11px", fontWeight: "700", marginBottom: "3px" }}>
                    Important Notice
                  </p>
                  <p style={{ color: "rgba(245,158,11,0.75)", fontSize: "11px", lineHeight: 1.6 }}>
                    Requests are processed within 24–48 hours. Minimum withdrawal is $10. Ensure your BEP20 address is correct.
                  </p>
                </div>
              </div>

              {/* Submit */}
              <button
                onClick={() => { if (validate()) setShowConfirm(true); }}
                disabled={submitting || isOverBalance}
                style={{
                  width: "100%", padding: "17px",
                  background: isOverBalance
                    ? "rgba(255,82,82,0.12)"
                    : submitting ? "rgba(33,193,92,0.3)" : C.greenBtn,
                  border: isOverBalance ? "1px solid rgba(255,82,82,0.3)" : "none",
                  borderRadius: "14px",
                  color: isOverBalance ? C.red : "#fff",
                  fontSize: "16px", fontWeight: "700",
                  cursor: (submitting || isOverBalance) ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  boxShadow: (submitting || isOverBalance) ? "none" : "0 4px 20px rgba(0,180,190,0.3)",
                  letterSpacing: "0.3px",
                }}
              >
                {submitting ? (
                  <>
                    <span style={{
                      width: "16px", height: "16px", borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff",
                      display: "inline-block", animation: "spin 0.7s linear infinite",
                    }} />
                    Processing…
                  </>
                ) : (
                  <>⬆ Request Withdrawal</>
                )}
              </button>

              {/* SSL note */}
              <p style={{ textAlign: "center", color: C.textDim, fontSize: "11px", marginTop: "12px" }}>
                🔒 Secured with SSL encryption
              </p>
            </>
          )}
        </div>
      </div>

      {/* ── Confirm Modal ── */}
      {showConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)",
          backdropFilter: "blur(8px)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999, padding: "20px",
        }}>
          <div style={{
            background: C.cardBg, borderRadius: "20px",
            border: "1px solid rgba(0,229,255,0.3)",
            boxShadow: "0 0 40px rgba(0,180,190,0.15)",
            padding: "32px 24px", textAlign: "center",
            maxWidth: "320px", width: "100%",
          }}>
            <div style={{
              width: "64px", height: "64px", borderRadius: "50%",
              background: "rgba(0,229,255,0.08)", border: `2px solid ${C.cyan}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 18px", fontSize: "26px",
              boxShadow: "0 0 20px rgba(0,229,255,0.2)",
            }}>⬆</div>
            <h3 style={{ color: C.textPrimary, fontSize: "18px", fontWeight: "700", marginBottom: "10px" }}>
              Confirm Withdrawal
            </h3>
            <div style={{
              background: C.inputBg, borderRadius: "12px",
              border: "1px solid rgba(0,229,255,0.15)",
              padding: "14px", marginBottom: "20px", textAlign: "left",
            }}>
              {[
                { label: "Amount",  value: `$${Number(amount).toFixed(3)} USDT`, color: C.cyan },
                { label: "From",    value: "Fund Wallet",                         color: C.green },
                { label: "Network", value: "BEP20 (BSC)",                         color: C.amber },
                { label: "Address", value: walletAddress.length > 20
                    ? `${walletAddress.substring(0, 10)}…${walletAddress.slice(-6)}`
                    : walletAddress,
                  color: C.textMuted },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", alignItems: "center" }}>
                  <span style={{ color: C.textDim, fontSize: "12px" }}>{label}</span>
                  <span style={{ color: color || C.textPrimary, fontSize: "13px", fontWeight: "600" }}>{value}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  flex: 1, padding: "13px",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "12px", color: C.textMuted,
                  fontSize: "14px", fontWeight: "600", cursor: "pointer",
                }}
              >Cancel</button>
              <button
                onClick={handleSubmit}
                style={{
                  flex: 1, padding: "13px", background: C.greenBtn,
                  border: "none", borderRadius: "12px", color: "#fff",
                  fontSize: "14px", fontWeight: "700", cursor: "pointer",
                  boxShadow: "0 4px 16px rgba(0,180,190,0.3)",
                }}
              >Confirm</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 6px #00e5ff; opacity: 1; }
          50%       { box-shadow: 0 0 14px #00e5ff; opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}

// ── Shared micro-styles ───────────────────────────────────────────────────────
const s = {
  label: {
    color: "#9ca3af", fontSize: "12px", fontWeight: "600",
    display: "block", marginBottom: "7px",
  },
  input: {
    width: "100%", padding: "14px 16px",
    background: "#0a0f15",
    border: "1px solid rgba(0,229,255,0.2)",
    borderRadius: "12px", color: "#ffffff",
    fontSize: "15px", fontWeight: "600", outline: "none",
    boxSizing: "border-box", fontFamily: "inherit",
    transition: "border-color 0.2s",
  },
  infoChip: {
    background: "rgba(0,229,255,0.05)",
    border: "1px solid rgba(0,229,255,0.12)",
    borderRadius: "8px", padding: "6px 8px",
    display: "flex", flexDirection: "column", gap: "2px",
  },
  chipLabel: { color: "#6b7280", fontSize: "9px", letterSpacing: "0.06em", textTransform: "uppercase" },
  chipVal:   { color: "#9ca3af", fontSize: "12px", fontWeight: "700", fontFamily: "monospace" },
};
